/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useGetUserDetailsQuery } from "./lib/apiSlice/users/userSlice.tsx";
import { useGetUnpaidInvoicesQuery } from "./lib/apiSlice/invoicing/invoicingSlice.tsx";
import loaderImageGif from "../../assets/img/kodi_loader.gif";
import { useGetLandlordSubscriptionStatusQuery } from "./lib/apiSlice/subscription/subscriptionSlice.tsx";
import NewSubscription from "./admin/subscriptions/newSubscription.tsx";
import InvoicesSubscription from "./admin/subscriptions/subscriptionInvoices.tsx";
import { useGetCompanyPropertyDetailsQuery } from "./lib/apiSlice/property/propertySlice.tsx";

// The backend's company_subscription_required response currently controls access.
// Set this to true to reconnect the legacy subscription prompts.
const ENABLE_LEGACY_SUBSCRIPTION_UI = false;

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Force fresh data on mount and route changes
  const {
    data: userData,
    isLoading: isUserLoading,
    refetch: refetchUser,
  } = useGetUserDetailsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: invoicingData,
    isLoading: isInvoicingLoading,
    refetch: refetchInvoices,
  } = useGetUnpaidInvoicesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: subscriptionData,
    isLoading: isSubscriptionLoading,
  } = useGetLandlordSubscriptionStatusQuery(undefined, {
    skip: !ENABLE_LEGACY_SUBSCRIPTION_UI,
  });

  // Get company details
  const {
    data: companyData,
    isLoading: isCompanyLoading,
    error: companyError,
    refetch: refetchCompany
  } = useGetCompanyPropertyDetailsQuery();

  // Additional refetch on route changes
  useEffect(() => {
    refetchUser();
    refetchInvoices();
    refetchCompany();
  }, [location.pathname, refetchUser, refetchInvoices, refetchCompany]);

  useEffect(() => {
    if (companyData?.results?.length > 0) {
      setCompanyDetails(companyData.results[0]);
    }
  }, [companyData]);

  useEffect(() => {
    if (!ENABLE_LEGACY_SUBSCRIPTION_UI) return;

    if (subscriptionData && !isSubscriptionLoading && companyDetails) {
      if (!subscriptionData.has_subscription) {
        setShowNewPlanModal(true);
        setShowSubscriptionModal(false);
        setShowSubscriptionWarning(true);
      } else if (!subscriptionData.paid) {
        setShowSubscriptionModal(true);
        setShowNewPlanModal(false);
        setShowSubscriptionWarning(true);
      } else {
        setShowNewPlanModal(false);
        setShowSubscriptionModal(false);
        setShowSubscriptionWarning(false);
      }
    }
  }, [subscriptionData, isSubscriptionLoading, companyDetails]);

  const user = userData || null;
  const unpaidInvoices = invoicingData?.unpaid_count || 0;
  const isLoading =
    isUserLoading ||
    isInvoicingLoading ||
    isCompanyLoading ||
    (ENABLE_LEGACY_SUBSCRIPTION_UI && isSubscriptionLoading);
  const companyErrorData =
    companyError?.data?.code
      ? companyError.data
      : companyError?.data?.data?.code
        ? companyError.data.data
        : null;
  const isCompanySubscriptionRequired =
    companyErrorData?.code === "company_subscription_required";
  const formatMoney = (value) =>
    new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    }).format(Number(value || 0));
  const formatDate = (value) =>
    value
      ? new Intl.DateTimeFormat("en-KE", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(new Date(`${value}T00:00:00`))
      : "Not specified";

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <img src={loaderImageGif} alt="Loading..." style={{ width: "400px" }} />
      </div>
    );
  }

  // Show error if company details failed to load
  if (companyError) {
    if (isCompanySubscriptionRequired) {
      return (
        <div className="flex justify-center items-center bg-slate-50 p-4 min-h-screen">
          <div className="bg-white shadow-lg border border-amber-200 rounded-xl w-full max-w-xl overflow-hidden">
            <div className="bg-amber-50 p-6 border-amber-200 border-b">
              <div className="flex items-start gap-3">
                <div className="flex justify-center items-center bg-amber-100 rounded-full w-11 h-11 text-amber-700 text-xl">
                  <i className="bi bi-credit-card"></i>
                </div>
                <div>
                  <h2 className="font-bold text-slate-900 text-xl">Subscription payment required</h2>
                  <p className="mt-1 text-slate-600">
                    Access to {companyErrorData.company_name} is paused because its subscription payment is overdue.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5 p-6">
              <div className="gap-3 grid grid-cols-2 text-sm">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-slate-500">Billing period</p>
                  <p className="font-semibold text-slate-900 capitalize">{companyErrorData.billing_period}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-slate-500">Payment due</p>
                  <p className="font-semibold text-slate-900">{formatDate(companyErrorData.due_date)}</p>
                </div>
                <div className="col-span-2 bg-red-50 p-3 rounded-lg">
                  <p className="text-red-600">Outstanding balance</p>
                  <p className="font-bold text-red-800 text-xl">
                    {formatMoney(companyErrorData.outstanding_amount)}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="mb-2 font-semibold text-slate-900">Pay via M-PESA</h3>
                <div className="space-y-2 bg-blue-50 p-4 border border-blue-100 rounded-lg text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-600">Paybill</span>
                    <strong>{companyErrorData.payment?.business_number}</strong>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-600">Account number</span>
                    <strong>{companyErrorData.payment?.account_number}</strong>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-600">Amount</span>
                    <strong>{formatMoney(companyErrorData.outstanding_amount)}</strong>
                  </div>
                </div>
              </div>

              <p className="text-slate-500 text-sm">
                After payment is confirmed, select “Check payment status” to restore access.
              </p>

              <div className="flex sm:flex-row flex-col gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-blue-600 hover:bg-blue-700 px-5 py-2.5 rounded-lg font-medium text-white"
                >
                  Check payment status
                </button>
                <button
                  onClick={() => {
                    localStorage.removeItem("authTokens");
                    window.location.replace("/#/login");
                  }}
                  className="bg-white hover:bg-slate-50 px-5 py-2.5 border border-slate-300 rounded-lg font-medium text-slate-700"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="bg-red-50 p-6 border border-red-200 rounded-lg max-w-md">
          <h2 className="mb-2 font-bold text-red-800 text-xl">Error Loading Company Details</h2>
          <p className="mb-4 text-red-600">
            Unable to load company information. Please refresh the page or contact support.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-white"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex bg-gray-50" style={{ height: "125vh" }}>
      {sidebarOpen && (
        <div
          className="md:hidden z-20 fixed inset-0 bg-black bg-opacity-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {ENABLE_LEGACY_SUBSCRIPTION_UI && showSubscriptionWarning ? (
        <div className="z-40 fixed inset-0 flex justify-center items-center">
          <div className="bg-white shadow-lg mx-4 p-6 border border-red-300 rounded-lg max-w-md">
            <p className="mb-4 font-medium text-red-600 text-lg text-center">
              Your subscription needs to be cleared to access all features.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center bg-red-600 hover:bg-red-700 px-6 py-2 rounded-lg font-medium text-white transition-colors"
              >
                <svg className="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <Sidebar
            isOpen={sidebarOpen}
            toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          />

          <div className="flex flex-col flex-1 overflow-hidden">
            <Header
              user={user}
              toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              unpaidInvoices={unpaidInvoices}
            />

            <main className="flex-1 p-2 md:p-2 overflow-y-auto">
              <div className="max-w-8xl">
                <Outlet />
              </div>
            </main>
          </div>
        </>
      )}

      {ENABLE_LEGACY_SUBSCRIPTION_UI && showNewPlanModal && companyDetails && (
        <NewSubscription
          isOpen={showNewPlanModal}
          onClose={() => setShowNewPlanModal(false)}
          companyDetails={companyDetails}
        />
      )}

      {ENABLE_LEGACY_SUBSCRIPTION_UI && showSubscriptionModal && (
        <InvoicesSubscription
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          invoices={subscriptionData?.invoices}
          user={user}
        />
      )}
    </div>
  );
}
